import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import { hasPermission } from "../../helpers/selectors";
import { formatIso, formatRelative } from "../../helpers/date";
import {
  bindKmsKey,
  getMe,
  listKmsBindings,
  migrateDaariSecrets,
  revokeKmsBinding,
  verifyKmsKey,
} from "./api";

/**
 * SCK-631 — Access → Security → Customer-Managed Keys.
 *
 * Read: GET /account/kms — list bindings owned by the caller's account.
 * Write (ACCOUNT_ADMIN):
 *   POST /account/kms/verify    — probe a KEK ARN end-to-end
 *   POST /account/kms/bind      — persist a binding after a green verify
 *   POST /account/kms/{id}/revoke — flip to REVOKED
 *   POST /daari/secrets/migrate — re-seal the account's plaintext secrets
 *                                 under a customer KEK (SCK-573 slice B)
 *
 * Non-admin callers see the same inline "only account admins" fallback
 * the sibling Invitations tab renders. A 403 from any endpoint bubbles
 * to the same alert — the page never shows an unhelpful red toast on
 * an authorization miss.
 */

const PROVIDER_OPTIONS = [
  { value: "aws", label: "AWS KMS" },
  { value: "gcp", label: "Google Cloud KMS (coming soon)", disabled: true },
  { value: "azure", label: "Azure Key Vault (coming soon)", disabled: true },
];

const VERIFY_ERROR_COPY = {
  missing_permissions:
    "The KEK exists, but your grant document does not allow Encrypt + Decrypt from the platform. Update the grantee principal and try again.",
  invalid_arn:
    "The ARN could not be resolved. Double-check the region + key id.",
  region_unreachable:
    "The platform could not reach the KEK's region. Confirm the region is enabled on your account.",
  provider_unsupported:
    "This provider is not supported yet — only AWS KMS is available today.",
  config_disabled:
    "BYOK is not enabled on this deployment. Contact support to turn it on.",
  unknown_error:
    "Verification failed with an unexpected error. See the details below.",
};

/**
 * Mask an ARN to its last 8 characters for the list view. Never used
 * on the form or copy path — the raw value is fine there because the
 * caller already has read access to the row.
 */
function maskArn(arn) {
  if (!arn) return "";
  if (arn.length <= 8) return arn;
  return "…" + arn.slice(-8);
}

/**
 * Map a status string to an MUI Chip color. VERIFICATION_FAILED is
 * reserved for a future write path that persists probe failures for
 * audit; today the page never writes that value but the map is here
 * so it renders consistently if a coord backfill ever surfaces one.
 */
function statusColor(status) {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "REVOKED":
      return "default";
    case "VERIFICATION_FAILED":
      return "error";
    default:
      return "default";
  }
}

export default function CmkPage({ currentUser }) {
  const theme = useTheme();

  const canView = hasPermission(currentUser, ["SYSTEM", "ACCOUNT_ADMIN"]);
  const isSystem = currentUser?.userType === "SYSTEM";

  const [accountId, setAccountId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Add-key form state
  const [addOpen, setAddOpen] = useState(false);
  const [provider, setProvider] = useState("aws");
  const [arnInput, setArnInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [binding, setBinding] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // null | { ok, fingerprint, ... } | { ok:false, error }

  // Revoke state
  const [revoking, setRevoking] = useState(null); // null | row

  // Migrate state
  const [migrateArn, setMigrateArn] = useState("");
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrateError, setMigrateError] = useState(null);

  const refresh = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const self = await getMe().catch(() => null);
      if (self && self.id) setAccountId(self.id);
      const items = await listKmsBindings();
      setRows(items);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reset the form + verify result whenever the dialog opens or the
  // ARN changes — a stale green fingerprint from a previous KEK MUST
  // NOT enable the Bind button once the user edits the input.
  useEffect(() => {
    if (!addOpen) {
      setProvider("aws");
      setArnInput("");
      setLabelInput("");
      setVerifyResult(null);
      setVerifying(false);
      setBinding(false);
    }
  }, [addOpen]);

  const activeBindings = useMemo(
    () => rows.filter((r) => r.status === "ACTIVE"),
    [rows]
  );

  const canBind = !!verifyResult?.ok && !!arnInput.trim() && !verifying && !binding;

  const runVerify = useCallback(async () => {
    if (!arnInput.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await verifyKmsKey({ provider, kekArn: arnInput.trim() });
      setVerifyResult(r);
    } catch (e) {
      // 403 / 500 — the server never SHOULD return this for a verify,
      // but if it does we surface an unknown_error shape so the modal
      // renders consistently.
      setVerifyResult({
        ok: false,
        error: e.status === 403 ? "missing_permissions" : "unknown_error",
        errorCode: String(e.status || ""),
      });
    } finally {
      setVerifying(false);
    }
  }, [arnInput, provider]);

  const runBind = useCallback(async () => {
    if (!canBind) return;
    setBinding(true);
    try {
      await bindKmsKey({
        provider,
        kekArn: arnInput.trim(),
        label: labelInput.trim() || null,
      });
      setToast({ severity: "success", message: "Key bound to this account." });
      setAddOpen(false);
      refresh();
    } catch (e) {
      setToast({
        severity: "error",
        message:
          e.status === 403
            ? "Only account admins can bind keys."
            : `Bind failed: ${e.message}`,
      });
    } finally {
      setBinding(false);
    }
  }, [arnInput, canBind, labelInput, provider, refresh]);

  const runRevoke = useCallback(async () => {
    const row = revoking;
    if (!row) return;
    try {
      await revokeKmsBinding(row.id);
      setToast({ severity: "success", message: "Key revoked." });
      setRevoking(null);
      refresh();
    } catch (e) {
      setToast({
        severity: "error",
        message:
          e.status === 403
            ? "Only account admins can revoke keys."
            : `Revoke failed: ${e.message}`,
      });
    }
  }, [refresh, revoking]);

  const runMigrate = useCallback(async () => {
    setMigrating(true);
    setMigrateError(null);
    setMigrateResult(null);
    try {
      const r = await migrateDaariSecrets({
        accountId,
        customerKmsKey: migrateArn.trim(),
      });
      setMigrateResult(r);
    } catch (e) {
      setMigrateError(
        e.status === 403
          ? "Only platform operators can run this migration today. Contact support."
          : e.message
      );
    } finally {
      setMigrating(false);
    }
  }, [accountId, migrateArn]);

  // ---------- render ----------

  if (!canView) {
    return (
      <Alert
        severity="warning"
        icon={<LockOutlinedIcon fontSize="small" />}
        sx={{ mt: 2 }}
        data-testid="cmk-forbidden"
      >
        Only account admins can view customer-managed keys. Ask your account
        owner if you need access.
      </Alert>
    );
  }

  if (error && error.status === 403) {
    return (
      <Alert
        severity="warning"
        icon={<LockOutlinedIcon fontSize="small" />}
        sx={{ mt: 2 }}
        data-testid="cmk-forbidden"
      >
        Only account admins can view customer-managed keys.
      </Alert>
    );
  }

  return (
    <Box data-testid="cmk-page">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Customer-Managed Keys
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Bring your own KEK. Daari secrets on this account will be envelope-
            encrypted under the customer key(s) you bind here.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} data-testid="cmk-refresh">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            data-testid="cmk-add-open"
          >
            Add key
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      ) : rows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center", mb: 3 }}
          data-testid="cmk-empty"
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            No customer-managed keys yet.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Bind a KEK to start envelope-encrypting new Daari secrets.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small" aria-label="Customer-managed keys">
            <TableHead>
              <TableRow>
                <TableCell>Key (ARN)</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid="cmk-row"
                  data-status={row.status}
                >
                  <TableCell>
                    <Tooltip title={row.kekArn}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {maskArn(row.kekArn)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.label || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.provider?.toUpperCase() || "?"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={statusColor(row.status)}
                      data-testid="cmk-status"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatIso(row.addedAt)}>
                      <span>{formatRelative(row.addedAt)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {row.status === "ACTIVE" ? (
                      <Tooltip title="Revoke">
                        <IconButton
                          size="small"
                          onClick={() => setRevoking(row)}
                          data-testid="cmk-revoke-open"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Migration section — SCK-573 slice B. Only useful on an account
          that already has an active binding; we still render it always
          so an operator can eyeball the state before they bind. */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-testid="cmk-migrate-section">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Migrate existing secrets
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Re-seal plaintext Daari secrets on this account under a customer KEK.
          {activeBindings.length === 0
            ? " Bind a key above before running this."
            : ""}
          {!isSystem
            ? " This action is currently platform-operator only — contact support to run it."
            : ""}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <FormControl fullWidth size="small">
            <Select
              value={migrateArn}
              onChange={(e) => setMigrateArn(e.target.value)}
              displayEmpty
              inputProps={{ "data-testid": "cmk-migrate-select" }}
            >
              <MenuItem value="">
                <em>Select a bound KEK…</em>
              </MenuItem>
              {activeBindings.map((b) => (
                <MenuItem key={b.id} value={b.kekArn}>
                  {maskArn(b.kekArn)} {b.label ? `— ${b.label}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={!migrateArn || migrating}
            onClick={runMigrate}
            data-testid="cmk-migrate-run"
          >
            {migrating ? "Running…" : "Migrate"}
          </Button>
        </Stack>
        {migrating ? (
          <LinearProgress sx={{ mt: 2 }} data-testid="cmk-migrate-progress" />
        ) : null}
        {migrateResult ? (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="cmk-migrate-result">
            Migration complete. Re-sealed {migrateResult.migratedCount ?? 0}{" "}
            secret(s); skipped {migrateResult.skippedCount ?? 0}
            {Array.isArray(migrateResult.errors) && migrateResult.errors.length > 0
              ? `; ${migrateResult.errors.length} error(s) — check the audit log.`
              : "."}
          </Alert>
        ) : null}
        {migrateError ? (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="cmk-migrate-error">
            {migrateError}
          </Alert>
        ) : null}
      </Paper>

      {/* Add-key dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="cmk-add-title"
      >
        <DialogTitle id="cmk-add-title">Add customer-managed key</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="cmk-add-provider-label">Provider</InputLabel>
              <Select
                labelId="cmk-add-provider-label"
                value={provider}
                label="Provider"
                onChange={(e) => {
                  setProvider(e.target.value);
                  setVerifyResult(null);
                }}
                inputProps={{ "data-testid": "cmk-add-provider" }}
              >
                {PROVIDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Key ARN"
              placeholder="arn:aws:kms:us-east-1:123456789012:key/…"
              value={arnInput}
              onChange={(e) => {
                setArnInput(e.target.value);
                // Editing the ARN invalidates any prior probe — a stale
                // green fingerprint MUST NOT enable Bind on a new value.
                setVerifyResult(null);
              }}
              fullWidth
              size="small"
              inputProps={{ "data-testid": "cmk-add-arn" }}
            />
            <TextField
              label="Label (optional)"
              placeholder="prod primary"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ "data-testid": "cmk-add-label" }}
            />
            {verifying ? (
              <LinearProgress data-testid="cmk-verify-progress" />
            ) : null}
            {verifyResult?.ok ? (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                data-testid="cmk-verify-success"
              >
                Verified. Fingerprint{" "}
                <code>{verifyResult.fingerprint || "—"}</code>
                {verifyResult.keyId ? (
                  <>
                    {" "}· KMS key id <code>{verifyResult.keyId}</code>
                  </>
                ) : null}
              </Alert>
            ) : null}
            {verifyResult && !verifyResult.ok ? (
              <Alert
                severity="error"
                icon={<ErrorOutlineIcon fontSize="inherit" />}
                data-testid="cmk-verify-error"
              >
                {VERIFY_ERROR_COPY[verifyResult.error] ||
                  `Verification failed (${verifyResult.error || "unknown"}).`}
                {verifyResult.errorCode ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    Code: <code>{verifyResult.errorCode}</code>
                  </Typography>
                ) : null}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            onClick={runVerify}
            disabled={verifying || !arnInput.trim()}
            data-testid="cmk-verify-run"
          >
            {verifying ? "Verifying…" : "Verify"}
          </Button>
          <Button
            variant="contained"
            onClick={runBind}
            disabled={!canBind}
            data-testid="cmk-bind-run"
          >
            {binding ? "Binding…" : "Bind"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        aria-labelledby="cmk-revoke-title"
      >
        <DialogTitle id="cmk-revoke-title">Revoke this key?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            You are about to revoke{" "}
            <code>{maskArn(revoking?.kekArn)}</code>
            {revoking?.label ? ` (${revoking.label})` : ""}. This cannot be
            undone from the console.
          </DialogContentText>
          <Alert severity="warning" data-testid="cmk-revoke-warning">
            <strong>In-flight secrets encrypted with this key will fail to
            decrypt.</strong>{" "}
            Rotate any workload that depends on secrets sealed under this KEK
            before revoking.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={runRevoke}
            data-testid="cmk-revoke-confirm"
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}
